import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Layout from '../components/Layout';

// ── Brand tokens ─────────────────────────────────────────────────────────────
const TEAL      = '#20C997';
const DARK_BLUE = '#003479';

// ── SVG Ring ─────────────────────────────────────────────────────────────────

const RingChart = ({ pct, label }) => {
  const size   = 130;
  const stroke = 11;
  const r      = (size - stroke) / 2;
  const circ   = 2 * Math.PI * r;
  const val    = Math.min(Math.max(pct || 0, 0), 100);
  const offset = circ - (val / 100) * circ;

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Track */}
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
          {/* Progress */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none"
            stroke={val === 0 ? '#e5e7eb' : TEAL}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${circ}`}
            strokeDashoffset={`${offset}`}
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 1s ease' }}
          />
          {/* Center value */}
          <text
            x={size / 2} y={size / 2 - 6}
            textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: 24, fontWeight: 800, fill: val === 0 ? '#d1d5db' : '#111827' }}
          >
            {val}
          </text>
          <text
            x={size / 2} y={size / 2 + 16}
            textAnchor="middle"
            style={{ fontSize: 11, fill: '#9ca3af' }}
          >
            out of 100
          </text>
        </svg>
      </div>
      <p className="text-sm font-semibold text-gray-700 text-center leading-tight max-w-[110px]">{label}</p>
    </div>
  );
};

// ── Subtopic pill ─────────────────────────────────────────────────────────────

const statusStyle = (status) => {
  switch (status) {
    case 'good':        return { background: '#d1fae5', color: '#065f46' };
    case 'needs_work':  return { background: '#fef3c7', color: '#92400e' };
    default:            return { background: '#f3f4f6', color: '#9ca3af' };
  }
};

const statusDot = (status) => {
  switch (status) {
    case 'good':        return '#10b981';
    case 'needs_work':  return '#f59e0b';
    default:            return '#d1d5db';
  }
};

const SubtopicPill = ({ sub }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
    style={statusStyle(sub.status)}
  >
    <span
      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
      style={{ background: statusDot(sub.status) }}
    />
    {sub.title}
  </span>
);

// ── Topic Row ────────────────────────────────────────────────────────────────

const TopicRow = ({ topic }) => {
  const hasScore = topic.score != null;
  const pct      = hasScore ? Math.round((topic.score / 10) * 100) : 0;

  return (
    <div className="py-4 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ background: hasScore ? DARK_BLUE : '#d1d5db' }}
        />
        <span className="flex-1 text-sm font-semibold text-gray-800">{topic.title}</span>
        <span className="text-sm font-bold ml-auto" style={{ color: hasScore ? DARK_BLUE : '#d1d5db' }}>
          {hasScore ? `${topic.score}/10` : '—'}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-2.5">
        <div
          className="h-2 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: DARK_BLUE, opacity: hasScore ? 1 : 0.2 }}
        />
      </div>

      {/* Subtopic pills */}
      {topic.subtopics?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {topic.subtopics.map((sub) => (
            <SubtopicPill key={sub.id} sub={sub} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Quick stat card ───────────────────────────────────────────────────────────

const StatCard = ({ value, label, sub, icon, color = TEAL }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex items-center gap-4">
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: `${color}18` }}
    >
      <svg className="w-6 h-6" style={{ color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
      </svg>
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonProgress = () => (
  <Layout>
    <div className="space-y-8 animate-pulse">
      <div className="h-8 bg-gray-200 rounded-xl w-48" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-44 bg-gray-100 rounded-2xl" />)}
      </div>
      <div className="h-64 bg-gray-100 rounded-2xl" />
      <div className="grid grid-cols-3 gap-4">
        {[1,2,3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-2xl" />)}
      </div>
    </div>
  </Layout>
);

// ── Main Component ────────────────────────────────────────────────────────────

const StudentProgress = () => {
  const { user } = useAuth();

  const [batches, setBatches]               = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [progress, setProgress]             = useState(null);
  const [attendance, setAttendance]         = useState(null);
  const [selfpaced, setSelfpaced]           = useState([]);
  const [pendingCount, setPendingCount]     = useState(0);

  const [loading, setLoading]               = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [error, setError]                   = useState('');

  // Initial parallel fetch
  useEffect(() => {
    Promise.all([
      api.get('/api/batches'),
      api.get(`/api/attendance/students/${user.id}`),
      api.get('/api/selfpaced'),
      api.get('/api/assignments'),
    ])
      .then(([bRes, aRes, spRes, assignRes]) => {
        const batchList = bRes.data?.batches || bRes.data || [];
        setBatches(batchList);
        if (batchList.length > 0) setSelectedBatchId(batchList[0].id);

        const records = aRes.data || [];
        const present = records.filter((r) => r.status === 'present' || r.status === 'late').length;
        setAttendance({
          total: records.length,
          present,
          rate: records.length > 0 ? Math.round((present / records.length) * 100) : 0,
        });

        setSelfpaced(spRes.data || []);
        setPendingCount((assignRes.data || []).length);
      })
      .catch(() => setError('Failed to load progress data.'))
      .finally(() => setLoading(false));
  }, [user.id]);

  // Fetch progress when batch changes
  useEffect(() => {
    if (!selectedBatchId) return;
    setProgressLoading(true);
    setProgress(null);
    api.get(`/api/progress/students/${user.id}/batches/${selectedBatchId}`)
      .then((res) => setProgress(res.data))
      .catch(() => setProgress(null))
      .finally(() => setProgressLoading(false));
  }, [selectedBatchId, user.id]);

  const spTotal = selfpaced.reduce((s, c) => s + (c.module_count || 0), 0);
  const spDone  = selfpaced.reduce((s, c) => s + (c.completed_count || 0), 0);

  const readiness = progress?.readiness;
  const rings = [
    { label: 'Interview Readiness',   pct: readiness?.interview_readiness   ?? 0 },
    { label: 'Resume Readiness',      pct: readiness?.resume_readiness      ?? 0 },
    { label: 'Job Readiness',         pct: readiness?.job_readiness         ?? 0 },
    { label: 'Communication Skills',  pct: readiness?.communication_skills  ?? 0 },
  ];

  const hasReadiness = readiness && Object.values(readiness).some((v) => v != null && v > 0);
  const hasTopics    = progress?.topics?.length > 0;

  if (loading) return <SkeletonProgress />;

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(32,201,151,0.12)' }}
            >
              <svg className="w-5 h-5" style={{ color: TEAL }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Progress</h1>
              <p className="text-sm text-gray-500">
                {progress ? `${progress.course_title} · ${progress.batch_name}` : 'Skill scores and readiness'}
              </p>
            </div>
          </div>

          {/* Batch selector (only if multiple batches) */}
          {batches.length > 1 && (
            <select
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:outline-none"
              style={{ focusRingColor: TEAL }}
            >
              {batches.map((b) => (
                <option key={b.id} value={b.id}>{b.batch_name}</option>
              ))}
            </select>
          )}
        </div>

        {batches.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No batch enrollment found</p>
              <p className="text-xs text-gray-400 mt-1">Contact your admin to get enrolled in a batch</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Readiness Rings ───────────────────────────────────────── */}
            <section>
              <div className="flex items-center gap-2 mb-5">
                <h2 className="text-lg font-bold text-gray-900">Readiness Overview</h2>
                {!hasReadiness && (
                  <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
                    Not yet evaluated
                  </span>
                )}
              </div>

              <div
                className="rounded-2xl p-6 sm:p-8"
                style={{ background: 'linear-gradient(135deg,rgba(0,52,121,0.04),rgba(32,201,151,0.06))' }}
              >
                {progressLoading ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="flex flex-col items-center gap-3">
                        <div className="w-32 h-32 rounded-full bg-gray-200 animate-pulse" />
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {rings.map((ring) => (
                      <div
                        key={ring.label}
                        className="bg-white rounded-2xl p-5 shadow-sm flex flex-col items-center border border-white/80"
                      >
                        <RingChart pct={ring.pct} label={ring.label} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* ── Topic Scores ──────────────────────────────────────────── */}
            <section>
              <h2 className="text-lg font-bold text-gray-900 mb-5">Skill Scores</h2>

              {progressLoading ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-5">
                  {[1,2,3].map(i => (
                    <div key={i} className="space-y-2">
                      <div className="flex justify-between">
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-12 bg-gray-200 rounded animate-pulse" />
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full animate-pulse" />
                      <div className="flex gap-2">
                        <div className="h-6 w-20 bg-gray-100 rounded-full animate-pulse" />
                        <div className="h-6 w-24 bg-gray-100 rounded-full animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : !hasTopics ? (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="flex flex-col items-center justify-center py-14 text-gray-400">
                    <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                      <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-500">No topics evaluated yet</p>
                    <p className="text-xs text-gray-400 mt-1">Your trainer will rate your skills soon</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-2">
                  {progress.topics.map((topic) => (
                    <TopicRow key={topic.id} topic={topic} />
                  ))}
                </div>
              )}
            </section>

            {/* ── Quick Stats ───────────────────────────────────────────── */}
            <section>
              <h2 className="text-lg font-bold text-gray-900 mb-5">Activity Stats</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                {attendance && (
                  <StatCard
                    value={`${attendance.rate}%`}
                    label="Attendance Rate"
                    sub={`${attendance.present} / ${attendance.total} classes`}
                    icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    color={TEAL}
                  />
                )}

                <StatCard
                  value={spTotal > 0 ? `${spDone}/${spTotal}` : '—'}
                  label="Self-Paced Modules"
                  sub={spTotal > 0 ? `${spTotal - spDone} remaining` : 'No courses available'}
                  icon="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  color={DARK_BLUE}
                />

                <StatCard
                  value={pendingCount}
                  label="Open Assignments"
                  sub={pendingCount === 0 ? 'All caught up!' : `${pendingCount} pending submission`}
                  icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  color={pendingCount === 0 ? TEAL : '#f59e0b'}
                />

              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StudentProgress;
